import { useEffect, useState } from 'react';

const PALETTE = [
  '#F472B6', // pink
  '#A855F7', // purple
  '#6366F1', // indigo
  '#0EA5E9', // sky
  '#10B981', // emerald
  '#F59E0B', // amber
  '#EF4444', // red
  '#14B8A6', // teal
  '#8B5CF6', // violet
] as const;

function hashDomain(domain: string): number {
  let hash = 5381;
  for (let i = 0; i < domain.length; i++) {
    hash = ((hash << 5) + hash + domain.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function colorFor(domain: string): string {
  return PALETTE[hashDomain(domain) % PALETTE.length];
}

interface HbsSiteIconProps {
  domain: string;
  /** Ordered list of favicon URLs to try. Items with multiple URLs (e.g.
   *  Twilio + SendGrid in one entry) pass one URL per identifier domain;
   *  the component walks through them on load failure before falling
   *  back to the colored letter tile. Backward-compatible with the
   *  single-URL case via a length-1 array. */
  faviconUrls?: string[];
  /** Pixel size of the square container. Default 32. */
  size?: number;
}

export function HbsSiteIcon({ domain, faviconUrls, size = 32 }: HbsSiteIconProps): JSX.Element {
  const radius = size <= 24 ? 7 : size <= 36 ? 9 : 11;
  const baseStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: `${radius}px`,
  };

  const urls = faviconUrls ?? [];
  const [idx, setIdx] = useState(0);
  // Reset when the input list changes (e.g. user navigates between items).
  useEffect(() => { setIdx(0); }, [urls.join('|')]);

  const currentUrl = urls[idx];
  if (currentUrl) {
    return (
      <img
        src={currentUrl}
        alt=""
        style={baseStyle}
        className="object-cover shrink-0 bg-hbs-surface-alt"
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }

  const letter = (domain[0] ?? '?').toUpperCase();
  const fontSize = Math.round(size * 0.42);

  return (
    <div
      style={{ ...baseStyle, backgroundColor: colorFor(domain), fontSize: `${fontSize}px` }}
      className="shrink-0 flex items-center justify-center font-bold text-white"
      aria-hidden="true"
    >
      {letter}
    </div>
  );
}
